import type { BatchAccount, BatchActivityBucket, BatchActivityCounts } from './types';
import type { Kitchen } from '../../libs';
import { dayInKitchen } from '../../libs/utils/kitchenDay';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;

export const bucketTotal = ({ userCreations, deviceCreations }: BatchActivityBucket): number => userCreations + deviceCreations;

/** The bucket with the most creations, the earliest on a tie; none when every bucket is empty. */
export const busiestBucket = (trend: BatchActivityBucket[]): { index: number; total: number } | undefined => {
  let busiest: { index: number; total: number } | undefined;
  for (const [index, bucket] of trend.entries()) {
    const total = bucketTotal(bucket);
    if (total > (busiest?.total ?? 0)) {
      busiest = { index, total };
    }
  }
  return busiest;
};

export const trendTotals = (trend: BatchActivityBucket[]): { accounts: number; handhelds: number } => ({
  accounts: trend.reduce((sum, bucket) => sum + bucket.userCreations, 0),
  handhelds: trend.reduce((sum, bucket) => sum + bucket.deviceCreations, 0)
});

/** The hour bucket holding `asOf`; none for day buckets or a window that does not reach it. */
export const currentHourIndex = (trend: BatchActivityBucket[], bucketSize: 'HOUR' | 'DAY', asOf: string): number | undefined => {
  if (bucketSize !== 'HOUR') {
    return undefined;
  }
  const at = Date.parse(asOf);
  const index = trend.findIndex(({ start }) => Date.parse(start) <= at && at < Date.parse(start) + MS_PER_HOUR);
  return index === -1 ? undefined : index;
};

export const createdBatches = ({ rootBatches, splitBatches }: BatchActivityCounts): number => rootBatches + splitBatches;

/** Whole minutes from `iso` to `asOf`; a record stamped after the read counts as just now. */
export const minutesBefore = (iso: string, asOf: string): number =>
  Math.max(0, Math.floor((Date.parse(asOf) - Date.parse(iso)) / MS_PER_MINUTE));

/** The most recent `lastRecordedAt` of the accounts; none without accounts. */
export const latestRecording = (accounts: BatchAccount[]): string | undefined =>
  accounts.reduce<string | undefined>(
    (latest, { lastRecordedAt }) =>
      latest === undefined || Date.parse(lastRecordedAt) > Date.parse(latest) ? lastRecordedAt : latest,
    undefined
  );

/** Whether `asOf` falls on the last day of the window, the only one a relative "13 min ago" still describes. */
export const windowReachesNow = (to: string, asOf: string, kitchen: Kitchen): boolean =>
  dayInKitchen(new Date(asOf), kitchen) === to;

/** The labels two or more accounts share: `accountId` alone tells those accounts apart. */
export const sharedLabels = (accounts: BatchAccount[]): Set<string> => {
  const seen = new Set<string>();
  const shared = new Set<string>();
  accounts.forEach(({ label }) => (seen.has(label) ? shared.add(label) : seen.add(label)));
  return shared;
};

const ID_PREFIX_LENGTH = 6;

/** `0b6f3c` of an account ID: what tells apart two accounts with one label. */
export const accountIdPrefix = (accountId: string): string => accountId.slice(0, ID_PREFIX_LENGTH);
